import React, { useEffect, useState } from "react";
import { Button, Form, Input, message, Modal, Steps, Tooltip } from "antd";
import { LeftOutlined, RightOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import { getConfig } from "../../../../../config";
import { TaskData } from "../../../../../types/TaskData";
import { ViewType } from "../../../../../types/ViewType";
import ConstructionOrderCreation from "../../../../internalOrders/constructionOrder/constructionOrderCreation/ConstructionOrderCreation";
import MeasureOrderCreation from "../../../../internalOrders/measureOrder/measureOrderCreation/MeasureOrderCreation";
import dayjs from "dayjs";
import {JobCode} from "../../../../../types/JobCodes";

const { Step } = Steps;
const { API_BASE_URL } = getConfig();

interface Task {
    taskType: string;
}

interface TransferOrderCardEditWizardProps {
    visible: boolean;
    onClose: () => void;
    selectedOrderCard: OrderCardOverviewData;
    selectedProject: string;
    tasks: LegacyTask[];
}

const TransferOrderCardEditWizard: React.FC<TransferOrderCardEditWizardProps> = ({
                                                                                     visible,
                                                                                     onClose,
                                                                                     selectedOrderCard,
                                                                                     selectedProject,
                                                                                     tasks,
                                                                                 }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [measuringTaskData, setMeasuringTaskData] = useState<TaskData[]>([]);
    const [form] = Form.useForm();
    const [intermediateData, setIntermediateData] = useState<any>({});
    const [documentationData, setDocumentationData] = useState({
        measuringLink: "",
        comment: "",
    });
    const [isSaveDisabled, setIsSaveDisabled] = useState(true);

    useEffect(() => {
        if (!visible) {
            setCurrentStep(0);
            form.resetFields();
            setIntermediateData({});
        }
    }, [visible]);

    const isStepComplete = () => {
        const task = tasks[currentStep];

        if (!task) return false;

        if (task.code === "DOCUMENTATION_MEASURING") {
            return documentationData.measuringLink.trim() !== "" && documentationData.comment.trim() !== "";
        }

        if (task.code === "CONSTRUCTION_ASSIGNMENT" || task.code === "MEASURING_ASSIGNMENT") {
            return !!intermediateData[task.code];
        }

        return true;
    };

    useEffect(() => {
        const fetchInsideOutside = async () => {
            if (!selectedOrderCard?.productCode) return;
            try {
                const res = await axios.get<JobCode>(`${API_BASE_URL}/getNetworkPlanSingleProductByCodeAndProject`, {
                    params: {
                        jobCode: selectedOrderCard.productCode,
                        projectNo: selectedProject
                    }
                });
                const job = res.data;
                setMeasuringTaskData([
                    {
                        jobCode: selectedOrderCard.cardName,
                        insideOutside: job.installationLocation ?? "Innen",
                        id: 0,
                        name: "",
                        category: "",
                        orderCardId: "",
                        ogl: null,
                        oglDesc: null,
                        jobCodeDesc: null,
                        duration: 0,
                        leadDuration: 0,
                        projectNo: "",
                        activityId: 0,
                        plannedOnBoardText: "",
                        activityDoneDate: null,
                        activityDesc: "",
                        activityStatus: "",
                        totalBuffer: 0,
                        department: "",
                        measureDate: "",
                        timestamp: 0,
                        faz: "",
                        saz: "",
                        fez: "",
                        sez: "",
                        persistanceUnit: "",
                        korrex: "",
                        protocolDesc: "",
                        predecessor: ""
                    }
                ]);
            } catch (err) {
                message.error("Fehler beim Laden der Produktdaten.");
            }
        };

        fetchInsideOutside();
    }, [selectedOrderCard?.productCode, selectedProject]);

    useEffect(() => {
        const hasDocumentationStep = tasks.some((task) => task.code === "DOCUMENTATION_MEASURING");
        const hasAssignmentSteps = tasks.some(
            (task) => task.code === "CONSTRUCTION_ASSIGNMENT" || task.code === "MEASURING_ASSIGNMENT"
        );
        const isDocumentationComplete = hasDocumentationStep
            ? documentationData.measuringLink.trim() !== "" && documentationData.comment.trim() !== ""
            : true;
        const isAssignmentsComplete = hasAssignmentSteps
            ? tasks
                .filter((task) => task.code === "CONSTRUCTION_ASSIGNMENT" || task.code === "MEASURING_ASSIGNMENT")
                .every((task) => intermediateData[task.code])
            : true;
        setIsSaveDisabled(!(isDocumentationComplete && isAssignmentsComplete));
    }, [documentationData, intermediateData, tasks]);

    const handleNext = () => {
        if (isStepComplete()) {
            setCurrentStep((prev) => Math.min(prev + 1, tasks.length - 1));
        } else {
            message.warning("Bitte fülle alle erforderlichen Felder aus.");
        }
    };

    const handlePrev = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const handleIntermediateSave = (taskType: string, description: string, data: any) => {
        setIntermediateData((prev: any) => ({
            ...prev,
            [taskType]: data,
        }));
        message.success(`Daten für '${description}' zwischengespeichert`);
    };

    const handleValidatedInput = (value: string): string => {
        if (value === "h") {
            return "helios://";
        } else if (value.startsWith("helios://") || validateHeliosUrl(value)) {
            return value;
        } else {
            message.error("Bitte gebe eine gültige Helios-URL an");
            return ""; // Reset to an empty string if invalid
        }
    };

    const validateHeliosUrl = (value: string): boolean => {
        return value.startsWith("helios://") && value.length > "helios://".length;
    };

    const handleFinalSave = async () => {
        Modal.confirm({
            width: 600,
            title: "Willst Du die Daten speichern?",
            content: "Die Änderungen werden gespeichert und der Wizard geschlossen.",
            okText: "Ja",
            cancelText: "Abbrechen",
            onOk: async () => {
                setIsLoading(true);
                try {
                    const constructionAssignments = Object.entries(intermediateData)
                        .filter(([key, _]) => key === "CONSTRUCTION_ASSIGNMENT")
                        .map(([_, data]) => data);
                    const measuringAssignments = Object.entries(intermediateData)
                        .filter(([key, _]) => key === "MEASURING_ASSIGNMENT")
                        .map(([_, data]) => data);
                    const { tasks, ...orderCardWithoutTasks } = selectedOrderCard;
                    const payload = {
                        projectNo: selectedProject,
                        orderCard: orderCardWithoutTasks,
                        documentation: documentationData,
                        constructionAssignments: constructionAssignments.length > 0 ? constructionAssignments : undefined,
                        measuringAssignments: measuringAssignments.length > 0 ? measuringAssignments : undefined,
                    };
                    console.log("🚀 Sende Payload:", JSON.stringify(payload, null, 2));
                    await axios.post(`${API_BASE_URL}/saveTransferProjectOrderCardStep`, payload);
                    message.success("Daten erfolgreich gespeichert.");
                    onClose();
                } catch (error) {
                    message.error("Fehler beim Speichern der Daten.");
                } finally {
                    setIsLoading(false);
                }
            },
        });
    };

    const renderStepContent = (task: LegacyTask) => {
        switch (task.code) {
            case "DOCUMENTATION_MEASURING":
                return (
                    <Form form={form} layout="vertical">
                        <Form.Item label="Verlinkung Bemassung">
                            <Input
                                placeholder="Helios-URL"
                                value={documentationData.measuringLink}
                                onChange={(e) => {
                                    const newValue = handleValidatedInput(e.target.value);
                                    setDocumentationData((prev) => ({
                                        ...prev,
                                        measuringLink: newValue,
                                    }));
                                }}
                            />
                        </Form.Item>
                        <Form.Item label="Kommentar">
                            <Input
                                value={documentationData.comment}
                                onChange={(e) =>
                                    setDocumentationData((prev) => ({
                                        ...prev,
                                        comment: e.target.value,
                                    }))
                                }
                            />
                        </Form.Item>
                    </Form>
                );

            case "CONSTRUCTION_ASSIGNMENT":
                return (
                    <ConstructionOrderCreation
                        data={[{
                            id: 1,
                            jobCode: selectedOrderCard.productCode,
                            faz: dayjs().format("YYYY-MM-DD"),
                            fez: dayjs().format("YYYY-MM-DD"),
                            totalBuffer: 10,
                        }] as TaskData[]}
                        viewType={ViewType.NEW_EXTERNAL}
                        projectNo={selectedProject}
                        onSave={(data) => handleIntermediateSave(task.code, task.description, data)}
                        measurementLinkNew={documentationData.measuringLink}
                    />
                );
            case "MEASURING_ASSIGNMENT":
                if (!measuringTaskData[0]?.insideOutside) {
                    return <p style={{ padding: 16 }}>Lade Messdaten...</p>;
                }

                return (
                    <MeasureOrderCreation
                        data={measuringTaskData}
                        viewType={ViewType.NEW_EXTERNAL}
                        onSave={(data) => handleIntermediateSave(task.code, task.description, data)}
                    />
                );
            default:
                return <p>Unbekannter Task-Typ: {task.code}</p>;
        }
    };

    return (
        <Modal
            title={`Bearbeiten: Karte ${selectedOrderCard.cardCode}  | ${selectedOrderCard.cardName}`}
            visible={visible}
            onCancel={onClose}
            footer={null}
            width="95vw"
            styles={{
                body: { height: "80vh", overflowY: "auto", padding: "20px" },
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
                <Tooltip title="Zurück">
                    <Button icon={<LeftOutlined />} onClick={handlePrev} disabled={currentStep === 0} />
                </Tooltip>
                <Steps current={currentStep} size="default" style={{ flex: 1, margin: "0 16px" }}>
                    {tasks.map((task, index) => (
                        <Step key={index} title={task.description} />
                    ))}
                </Steps>
                {currentStep === tasks.length - 1 ? (
                    <Tooltip title="Speichern & Schliessen">
                        <Button type="primary" onClick={handleFinalSave} loading={isLoading} disabled={isSaveDisabled} icon={<SaveOutlined />}>
                            {isLoading ? "Speichert..." : "Speichern & Schliessen"}
                        </Button>
                    </Tooltip>
                ) : (
                    <Tooltip title="Weiter">
                        <Button icon={<RightOutlined />} type="primary" onClick={handleNext} disabled={!isStepComplete()} />
                    </Tooltip>
                )}
            </div>
            <div>{renderStepContent(tasks[currentStep])}</div>
        </Modal>
    );
};

export default TransferOrderCardEditWizard;
